/* eslint-disable css-modules/no-undef-class */
import React from 'react';
import { GetServerSideProps, NextPage } from 'next';
import Link from 'next/link';
import { PRODUCER } from 'routes';

import { getProducerList } from 'api';
import { NotFoundError } from 'api/utils';
import Block from 'components/Block';
import Container from 'components/Container';
import MetaTitle from 'components/MetaTitle';
import Typography from 'components/Typography';
import Layout from 'containers/Layout';
import Main from 'containers/Main';
import styles from 'styles/pages/Producers.module.css';
import { ProducerDetail } from 'types/Producer';
import { chunkBy, cx } from 'utils';

const sortProducers = (producers: ProducerDetail[]) => {
  const sortedProducers = producers.sort((a, b) => a.name.localeCompare(b.name));

  const producersLetterMap: Record<string, ProducerDetail[]> = {};

  // Append producer to first letter of lastname
  sortedProducers.forEach(producer => {
    const lastNameFirstLetter = producer.name.substring(0, 1).toUpperCase();

    if (!producersLetterMap[lastNameFirstLetter]) {
      producersLetterMap[lastNameFirstLetter] = [];
    }

    producersLetterMap[lastNameFirstLetter].push(producer);
  });

  // Make producers iterable
  const producerEntries = Object.entries(producersLetterMap);

  return { producerEntries };
};

const makeProducerLinkProps = (producer: ProducerDetail) => ({
  passHref: true,
  href: {
    pathname: PRODUCER.pathname,
    query: { slug: producer.slug },
  },
});

const ProducerList: NextPage<{ producers: ProducerDetail[] }> = ({ producers }) => {
  const { producerEntries } = sortProducers(producers);

  return (
    <>
      <MetaTitle>Producers</MetaTitle>
      <Layout>
        <Main paddingTop>
          <Container>
            <div className={styles.allProducers}>
              {producerEntries.map(([letter, producers], index) => {
                const chunkedProducers = chunkBy(producers, Math.ceil(producers.length / 2));

                return (
                  <Block
                    id={letter}
                    className={cx(styles.block, styles.allProducersBlock)}
                    key={index}
                  >
                    <div className={styles.letterContainer}>
                      <Typography className={styles.textStyle}>{letter}</Typography>
                    </div>
                    <div className={styles.chunkContainer}>
                      {chunkedProducers.map((_producers, iindex) => {
                        return (
                          <div className={styles.chunk} key={iindex}>
                            {_producers.map(producer => (
                              <Link
                                {...makeProducerLinkProps(producer)}
                                key={producer.id}
                                legacyBehavior
                              >
                                <Typography
                                  uppercase
                                  component="a"
                                  className={styles.textStyle}
                                >{`${producer.name}`}</Typography>
                              </Link>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </Block>
                );
              })}
            </div>
          </Container>
        </Main>
      </Layout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async () => {
  try {
    const { data: producers } = await getProducerList();
    return {
      props: {
        producers,
      },
    };
  } catch (error) {
    if (error instanceof NotFoundError) {
      return {
        props: {
          producers: [],
        },
      };
    } else {
      throw error;
    }
  }
};

export default ProducerList;
